<template>
  <div id="app">
    <bg-video v-if="!isMemberPage" />
    <header>
      <div :class="{ 'card-lg': isMemberPage }">
        <div class="desktop">
          <flag-picker />
        </div>
        <router-link to="/" id="logo">
          <img src="./assets/logo.png" />
        </router-link>
        <div class="social-links desktop">
          <a href="https://www.facebook.com/equardian/" target="_blank"
            ><icon-facebook
          /></a>
          <a href="https://www.instagram.com/equardian/" target="_blank"
            ><icon-instagram
          /></a>
        </div>
      </div>
    </header>
    <main>
      <router-view />
    </main>
    <footer>
      <div>
        <p class="text-small">
          {{ `${__('Frågor')}? ${__('Ring')}` }}
          <span class="text-white">070-261 26 84</span>
          {{ `${__('eller skriv till ')}`
          }}<a href="mailto:support@equardian.se">support@equardian.se</a>
        </p>
        <div class="mobile">
          <flag-picker />
          <div class="social-links">
            <a href="https://www.facebook.com/equardian/" target="_blank"
              ><icon-facebook
            /></a>
            <a href="https://www.instagram.com/equardian/" target="_blank"
              ><icon-instagram
            /></a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>
<script>
import firestoreHandler from '@/services/firestoreHandler.js'
export default {
  components: {
    BgVideo: () => import('@/components/BackgroundVideo'),
    FlagPicker: () => import('@/components/FlagPicker'),
    IconFacebook: () => import('@/components/icons/IconFacebook'),
    IconInstagram: () => import('@/components/icons/IconInstagram'),
  },
  mounted () {
    this.getProducts()
  },
  computed: {
    isMemberPage () {
      return this.$route.path.includes('member')
    },
  },
  methods: {
    async getProducts () {
      const subscribeHandler = snapshot => {
        const sortedProducts = {}

        snapshot.docs.forEach(doc => {
          const p = doc.data()
          sortedProducts[p.id] = p
        })

        this.$store.commit('setProducts', sortedProducts)
      }

      await firestoreHandler.subscribe(
        {
          collectionPath: 'products',
        },
        subscribeHandler
      )
    },
  },
}
</script>
<style lang="less">
@import 'style/variables.less';

// Apply font-size on root to allow for rem
html {
  font-size: 14px;
}

// CSS reset for logical measurments (e.g. padding-left does not increase width)
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

#app {
  overflow-x: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  width: 100%;
  min-height: 100vh;
  font-family: 'OpenSans';
  color: @color-white-transparent;
  text-align: center;
  > header,
  > main,
  > footer {
    display: flex;
    margin: auto;
  }
  > main,
  > footer {
    flex: 1;
  }
  > header {
    min-height: 15vh;
    > div {
      display: flex;
      margin-top: 50px;
      width: @width-card-sm;
      max-width: 100vw;
      > * {
        flex: 1;
      }
      #logo {
        margin-top: -26px;
        align-self: flex-start;
        display: inline-block;
        img {
          width: 185px;
          max-width: 40vw;
        }
      }
    }
  }
  > footer {
    align-items: flex-end;
    > div {
      display: flex;
      flex-direction: column;
      margin-top: 2rem;
      width: 100vw;
      padding: 1em;
      background: @color-black-transparent;
    }
    .mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 10px;
      > * {
        flex: 1;
      }
    }
  }
  .social-links {
    text-align: right;
    .icon {
      padding: 2px;
      border-radius: 2px;
      height: 20px;
      margin-left: 10px;
    }
    .icon-instagram {
      border-radius: 5px;
    }
    .icon-instagram:hover {
      background: radial-gradient(
        circle at 30% 107%,
        #fdf497 0%,
        #fdf497 5%,
        #fd5949 45%,
        #d6249f 60%,
        #285aeb 90%
      );
    }
    .icon-facebook:hover {
      background: rgb(59, 89, 152);
    }
  }
}

// Override/fix default styling for various elements
button,
select,
input,
textarea {
  font-family: inherit;
  font-size: 1rem;
  font: inherit;
  border: none;
}
button {
  color: @color-white;
  background: transparent;
}

// Make sure no zoom is triggered on mobile devices
input,
textarea {
  font-size: 16px;
}

h1,
h2,
h3,
.button,
.header {
  font-family: Oswald;
  color: @color-white;
  font-weight: 500;
  margin-bottom: 1em;
}

h1 {
  font-size: 2.8rem;
}

h2 {
  font-size: 2rem;
}

h3 {
  font-size: 1.5rem;
}

a {
  text-decoration: none;
  color: @color-white;
}

a:not(.button):hover,
button:not(.button):hover {
  text-decoration: underline;
}

a,
input[type='checkbox'] + label,
button {
  cursor: pointer;
}

label,
.disable-user-select {
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -o-user-select: none;
}
button:focus {
  outline: 0;
}
.button {
  background: @color-orange;
  color: @color-white;
  padding: 0.7rem 0;
  width: 100%;
  text-transform: uppercase;
  font-weight: 500;
  border: 1px solid transparent;
  transition: 0.3s all;
  text-align: center;
}

.button-snd {
  background: transparent;
  color: @color-orange;
  border-color: @color-orange;
}

.button:disabled {
  opacity: 0.4;
}

.button:not(:disabled):hover {
  background: @color-grey-light;
  color: white;
  border-color: transparent;
}

input {
  background: rgba(255, 255, 255, 0.05);
  color: @color-white;
  padding: 1em;
  margin-bottom: 1.2em;
}

input:not([type='checkbox']) {
  border-bottom: 1px solid transparent;
  border-radius: 0;
  width: 100%;
}

input:not([type='checkbox']):focus {
  outline: none;
  border-color: @color-white-transparent;
}

/* No arrows for number input: Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* No arrows for number input: Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

// Global attributes
.desktop {
  display: none;
}

.mobile {
  display: initial;
}

.opacity {
  opacity: 0.4;
}

.text-orange {
  color: @color-orange;
}

.text-white {
  color: @color-white;
}

.fix-width {
  max-width: @width;
  margin: 0 auto;
}

.fix-width-lg {
  max-width: @width-lg;
  margin: 0 auto;
}

.text-small {
  font-size: 0.8rem;
}

.text-xs-small {
  font-size: 0.6rem;
}

.margin-bottom-x1 {
  margin-bottom: 20px !important;
}

.margin-bottom-x2 {
  margin-bottom: 30px !important;
}

.margin-bottom-x3 {
  margin-bottom: 40px !important;
}

// Global elements
.card {
  display: flex;
  position: relative;
  flex-direction: column;
  padding: 1rem;
  align-items: center;
  align-self: center;
  width: @width-card-sm;
  max-width: 100vw;
}

.row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}
.forced {
  flex-direction: row;
}

.el-loader__loader:before {
  background: @color-orange !important;
}

.checkboxes {
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 30px;
  width: auto;
  input {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    cursor: pointer;
  }
  .checkmark {
    position: absolute;
    left: 0px;
    height: 18px;
    width: 18px;
    border-radius: 2px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    transition: border 0.3s ease-out;
  }
  .checkmark:after {
    content: '';
    position: absolute;
    display: none;
  }
  input:checked ~ .checkmark:after {
    display: block;
  }
  input:checked ~ .checkmark {
    border-color: @color-orange;
  }
  .checkmark {
    transition: all 0.2s ease-in;
  }
  .checkmark:after {
    border: solid @color-orange;
    border-width: 0 3px 3px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
    height: 14px;
    width: 6px;
    top: 0;
    left: 5px;
  }
}
.checkboxes:hover {
  color: white;
  .checkmark {
    border-color: @color-orange;
  }
}

.radiobuttons {
  display: flex;
  flex-wrap: wrap;
  margin: 25px 0;
  .radio-item {
    display: inline-block;
    position: relative;
    margin-right: 20px;
    margin-bottom: 10px;
  }
  .radio-item input[type='radio'] {
    display: none;
  }

  .radio-item label:before {
    content: ' ';
    display: inline-block;
    position: relative;
    top: 3px;
    margin: 0 10px 0 0;
    width: 16px;
    height: 16px;
    border-radius: 11px;
    border: 1px solid @color-orange;
    background-color: transparent;
  }
  label {
    cursor: pointer;
  }
  .radio-item input[type='radio']:checked + label:after {
    border-radius: 11px;
    width: 10px;
    height: 10px;
    position: absolute;
    top: 7px;
    left: 3px;
    content: ' ';
    display: block;
    background: @color-orange;
  }
  .radio-item input[type='radio']:checked + label,
  .radio-item input[type='radio']:hover + label {
    color: white;
  }
}

.progress-bar {
  background: rgba(@color-orange, 0.7);
  position: absolute;
  bottom: 0;
  left: 0;
  width: 70%;
  height: 8px;
  transition: 0.5s all;
}

// Media queries
@media (min-width: @width-device-sm) {
  .card {
    padding: 14vw;
    background: @color-grey-dark;
  }
  #app > header {
    border-color: transparent;
  }
}

@media (min-width: @width-device-md) {
  html {
    font-size: 16px;
  }
  .desktop {
    display: initial;
  }
  .mobile {
    display: none !important;
  }
  .card {
    padding: 6rem;
  }
  .card-md {
    width: @width-card-md;
  }
  .card-lg {
    width: @width-card-lg !important;
  }
  .row {
    flex-direction: row;
    > *:not(:last-child) {
      margin-right: 0.4em;
    }
    > *:not(:first-child) {
      margin-left: 0.4em;
    }
  }
  .button {
    max-width: 220px;
  }
}
</style>
